import { useState, useEffect } from 'react';
import { Settings, User, Building, Bell, Shield, Globe, Save, CheckCircle } from 'lucide-react';
import { useI18n, LanguageToggle } from '../i18n';

import { API_BASE } from '../config/api';

export function SettingsPage() {
    const { t } = useI18n();
    const [activeTab, setActiveTab] = useState<'profile' | 'clinic' | 'notifications' | 'security'>('profile');
    const [saved, setSaved] = useState(false);

    const [profile, setProfile] = useState({
        firstName: 'Demo',
        lastName: 'Médico',
        email: 'doctor@clinicos.local',
        phone: '+573001234567',
        specialty: 'Medicina General',
        licenseNumber: 'MED-CO-12345',
    });

    const [clinic, setClinic] = useState({
        name: 'ClinicOS Demo Clinic',
        address: 'Calle 72 #10-34',
        city: 'Bogotá',
        phone: '+573001234567',
        timezone: 'America/Bogota',
    });

    const [notifications, setNotifications] = useState({
        emailAppointments: true,
        emailLabResults: true,
        whatsappReminders: true,
        soundAlerts: true,
    });

    async function handleSave() {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        try {
            await fetch(`${API_BASE}/staff/settings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ profile, clinic, notifications }),
            });
        } catch (err) {
            console.warn('Settings save pending (API may be unavailable):', err);
        }
    }

    const tabs = [
        { key: 'profile' as const, label: 'Perfil', icon: User },
        { key: 'clinic' as const, label: 'Clínica', icon: Building },
        { key: 'notifications' as const, label: 'Notificaciones', icon: Bell },
        { key: 'security' as const, label: 'Seguridad', icon: Shield },
    ];

    return (
        <div className="page-content fade-in">
            <div className="page-header" style={{ position: 'static', borderBottom: 'none', background: 'transparent', paddingLeft: 0 }}>
                <div>
                    <h1 className="page-title">{t('nav.settings')}</h1>
                    <p className="page-subtitle">Configuración del sistema</p>
                </div>
                <button className="btn btn-primary" onClick={handleSave} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {saved ? <><CheckCircle size={16} /> Guardado</> : <><Save size={16} /> Guardar cambios</>}
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 'var(--space-6)' }}>
                {/* Sidebar Tabs */}
                <div className="card" style={{ padding: 'var(--space-2)' }}>
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                                width: '100%', padding: 'var(--space-2) var(--space-3)',
                                borderRadius: 'var(--radius-md)', border: 'none',
                                background: activeTab === tab.key ? 'var(--color-primary)' : 'transparent',
                                color: activeTab === tab.key ? 'white' : 'var(--color-text-secondary)',
                                cursor: 'pointer', fontSize: 'var(--text-sm)', fontWeight: 500,
                                transition: 'all 0.15s',
                                textAlign: 'left',
                            }}
                        >
                            <tab.icon size={16} /> {tab.label}
                        </button>
                    ))}

                    <div style={{ margin: 'var(--space-4) var(--space-3) var(--space-2)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-3)' }}>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
                            <Globe size={12} style={{ display: 'inline', marginRight: 4 }} /> Idioma
                        </div>
                        <LanguageToggle />
                    </div>
                </div>

                {/* Content */}
                <div className="card">
                    {activeTab === 'profile' && (
                        <div>
                            <h2 className="card-title" style={{ marginBottom: 'var(--space-6)' }}>Perfil del doctor</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                                <div>
                                    <label className="form-label">Nombre</label>
                                    <input className="form-input" value={profile.firstName}
                                        onChange={e => setProfile(p => ({ ...p, firstName: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="form-label">Apellido</label>
                                    <input className="form-input" value={profile.lastName}
                                        onChange={e => setProfile(p => ({ ...p, lastName: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="form-label">Correo electrónico</label>
                                    <input className="form-input" type="email" value={profile.email}
                                        onChange={e => setProfile(p => ({ ...p, email: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="form-label">Teléfono</label>
                                    <input className="form-input" value={profile.phone}
                                        onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="form-label">Especialidad</label>
                                    <input className="form-input" value={profile.specialty}
                                        onChange={e => setProfile(p => ({ ...p, specialty: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="form-label">Número de licencia</label>
                                    <input className="form-input" value={profile.licenseNumber}
                                        onChange={e => setProfile(p => ({ ...p, licenseNumber: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'clinic' && (
                        <div>
                            <h2 className="card-title" style={{ marginBottom: 'var(--space-6)' }}>Configuración de la clínica</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                                <div style={{ gridColumn: '1 / -1' }}>
                                    <label className="form-label">Nombre de la clínica</label>
                                    <input className="form-input" value={clinic.name}
                                        onChange={e => setClinic(c => ({ ...c, name: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="form-label">Dirección</label>
                                    <input className="form-input" value={clinic.address}
                                        onChange={e => setClinic(c => ({ ...c, address: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="form-label">Ciudad</label>
                                    <input className="form-input" value={clinic.city}
                                        onChange={e => setClinic(c => ({ ...c, city: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="form-label">Teléfono</label>
                                    <input className="form-input" value={clinic.phone}
                                        onChange={e => setClinic(c => ({ ...c, phone: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="form-label">Zona horaria</label>
                                    <select className="form-input" value={clinic.timezone}
                                        onChange={e => setClinic(c => ({ ...c, timezone: e.target.value }))}>
                                        <option value="America/Bogota">América/Bogotá (COT)</option>
                                        <option value="America/Mexico_City">América/Ciudad de México (CST)</option>
                                        <option value="America/New_York">América/Nueva York (EST)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'notifications' && (
                        <div>
                            <h2 className="card-title" style={{ marginBottom: 'var(--space-6)' }}>Preferencias de notificaciones</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                {[
                                    { key: 'emailAppointments' as const, label: 'Notificaciones de citas por email', desc: 'Recibir un email cuando se agende, cancele o modifique una cita' },
                                    { key: 'emailLabResults' as const, label: 'Resultados de laboratorio por email', desc: 'Recibir un email cuando los resultados del paciente estén listos' },
                                    { key: 'whatsappReminders' as const, label: 'Recordatorios por WhatsApp', desc: 'Enviar recordatorios automáticos a los pacientes' },
                                    { key: 'soundAlerts' as const, label: 'Alertas de sonido', desc: 'Reproducir sonido al recibir notificaciones en la plataforma' },
                                ].map(item => (
                                    <div key={item.key} style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: 'var(--space-3)', borderRadius: 'var(--radius-md)',
                                        background: 'var(--color-surface-hover)',
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{item.label}</div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{item.desc}</div>
                                        </div>
                                        <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: 'pointer' }}>
                                            <input type="checkbox" checked={notifications[item.key]}
                                                onChange={e => setNotifications(n => ({ ...n, [item.key]: e.target.checked }))}
                                                style={{ opacity: 0, width: 0, height: 0 }}
                                            />
                                            <span style={{
                                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                                background: notifications[item.key] ? 'var(--color-primary)' : 'var(--color-border)',
                                                borderRadius: 12, transition: 'background 0.2s',
                                            }}>
                                                <span style={{
                                                    position: 'absolute', top: 2, left: notifications[item.key] ? 22 : 2,
                                                    width: 20, height: 20, borderRadius: '50%', background: 'white',
                                                    transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                }} />
                                            </span>
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div>
                            <h2 className="card-title" style={{ marginBottom: 'var(--space-6)' }}>Seguridad</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                <div className="card" style={{ background: 'var(--color-surface-hover)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <div style={{ fontWeight: 500 }}>Cambiar contraseña</div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                                                Gestionado por Keycloak SSO
                                            </div>
                                        </div>
                                        <a href="http://localhost:8180/realms/clinicos/account" target="_blank" rel="noopener noreferrer"
                                            className="btn btn-secondary btn-sm">
                                            Abrir Keycloak
                                        </a>
                                    </div>
                                </div>
                                <div className="card" style={{ background: 'var(--color-surface-hover)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <div style={{ fontWeight: 500 }}>Sesiones activas</div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                                                1 sesión activa en este navegador
                                            </div>
                                        </div>
                                        <span className="badge badge-success">Activa</span>
                                    </div>
                                </div>
                                <div className="card" style={{ background: 'var(--color-surface-hover)' }}>
                                    <div>
                                        <div style={{ fontWeight: 500, marginBottom: 8 }}>Log de auditoría</div>
                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                                            Todas las acciones clínicas son registradas automáticamente en el log de auditoría
                                            conforme a las regulaciones HIPAA. Los registros incluyen timestamp, usuario, acción,
                                            y recurso afectado.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
